import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { guardWorkspaceAccess } from "@/lib/workspaceGuard";
import { useFullscreenRoute } from "@/lib/routeChrome";
import { workspacesQueryOptions } from "@/lib/workspaces";
import { SuspensionBanner } from "@/components/quota/SuspensionBanner";
import { FromAddressBanner } from "@/components/settings/FromAddressBanner";
import { AccountMenu } from "@/components/dashboard/AccountMenu";
import { WorkspaceChrome } from "@/components/dashboard/WorkspaceChrome";
import { useActionSidebarCount } from "@/components/overview/useActionSidebarCount";
import { useViewBlogLink } from "@/lib/blog/viewBlogLink";

export const Route = createFileRoute("/dashboard/w/$workspaceId")({
  beforeLoad: guardWorkspaceAccess,
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const { workspaceId } = Route.useParams();
  // Route-declared chrome (design §2): the composer routes ask for no shell.
  // Read reactively — see `useFullscreenRoute`'s doc comment for why a
  // snapshot read would leave the shell gone after leaving the composer.
  const fullscreen = useFullscreenRoute();
  const { data: workspaces } = useQuery(workspacesQueryOptions);
  const currentWorkspace = workspaces?.find(
    (workspace) => workspace.workspaceId === workspaceId,
  );
  // The shell's workspace menu (#1014) is fed from HERE — the list is already
  // queried above for the current workspace's name, so the menu costs no
  // second read, and `AppShell`/`WorkspaceChrome` keep their fetch-nothing
  // contract. doc-08 §3's "no switcher" rule is superseded: the data model
  // always allowed several workspaces per user; only the UI refused to say so.
  const workspaceName = currentWorkspace?.name ?? "GoodSender";
  // The Dashboard nav badge (#639) — same selector the Dashboard's own
  // action sidebar uses, so the two can never disagree (D-638-2). The badge
  // ignores `isPending`: 0 renders no badge, which is already the honest
  // loading state (unlike the sidebar's fallback sentence — see `ActionSidebar`).
  const { count: actionSidebarCount, label: actionSidebarLabel } =
    useActionSidebarCount(workspaceId);
  // The sidebar's live-blog link (2026-08-29), read HERE for the same reason
  // the badge is: `AppShell` and `SidebarNav` render what they are given and
  // fetch nothing, so their unit tests need no query client. `null` while the
  // blog read is pending, errored or refused — the link is then not rendered.
  const viewBlog = useViewBlogLink(workspaceId);

  return (
    <WorkspaceChrome
      fullscreen={fullscreen}
      workspaceId={workspaceId}
      title={workspaceName}
      workspaces={workspaces ?? []}
      banner={
        <>
          <SuspensionBanner workspaceId={workspaceId} />
          <FromAddressBanner workspaceId={workspaceId} />
        </>
      }
      account={<AccountMenu workspaceId={workspaceId} />}
      actionSidebarCount={actionSidebarCount}
      actionSidebarLabel={actionSidebarLabel}
      viewBlog={viewBlog}
    >
      <Outlet />
    </WorkspaceChrome>
  );
}
